import React, { useState } from 'react';
import { useExamQuestionsList } from '@/hooks/queries/exam/useList';
import {
  useExamAddQuestions,
  useExamQuestionUpdate,
  useExamQuestionDelete,
} from '@/hooks/mutations/exam';
import { ExamQuestion, ExamQuestionInput, ExamQuestionOption } from '@/types/lastexam';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';
import { Plus, Edit2, Trash2, ArrowLeft, CheckCircle2, HelpCircle, Image as ImageIcon } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/utils/styleUtils';
import { Pagination } from '@/components/Pagination';

interface ExamQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  examId: string;
  examName?: string;
}

type Translation = { locale: 'uz' | 'ru' | 'en'; text: string };
type OptionForm = {
  id?: string;
  isCorrect: boolean;
  image?: string;
  translations: Translation[];
};

export const ExamQuestionsModal: React.FC<ExamQuestionsModalProps> = ({
  isOpen,
  onClose,
  examId,
  examName,
}) => {
  const { toast } = useToast();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [selectedQuestion, setSelectedQuestion] = useState<ExamQuestion | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<ExamQuestion | null>(null);
  const [page, setPage] = useState(1);

  // Form state
  const [type, setType] = useState<'SINGLE_CHOICE' | 'MULTIPLE_CHOICE'>('SINGLE_CHOICE');
  const [points, setPoints] = useState<number>(1);
  const [questionImage, setQuestionImage] = useState<string>('');
  const [questionTranslations, setQuestionTranslations] = useState<Translation[]>([
    { locale: 'uz', text: '' },
    { locale: 'ru', text: '' },
    { locale: 'en', text: '' },
  ]);
  const [options, setOptions] = useState<OptionForm[]>([]);

  // Query & Mutations
  const { data: questionsData, isLoading, refetch } = useExamQuestionsList({
    testId: examId,
    pageNumber: page,
    pageSize: 20,
  });

  const { mutate: addQuestions, isPending: isAdding } = useExamAddQuestions();
  const { mutate: updateQuestion, isPending: isUpdating } = useExamQuestionUpdate();
  const { mutate: deleteQuestion, isPending: isDeleting } = useExamQuestionDelete();

  const questionsList: ExamQuestion[] =
    questionsData?.data?.data || questionsData?.data || [];

  const handleOpenCreate = () => {
    setSelectedQuestion(null);
    setType('SINGLE_CHOICE');
    setPoints(1);
    setQuestionImage('');
    setQuestionTranslations([
      { locale: 'uz', text: '' },
      { locale: 'ru', text: '' },
      { locale: 'en', text: '' },
    ]);
    setOptions([
      {
        isCorrect: true,
        image: '',
        translations: [
          { locale: 'uz', text: '' },
          { locale: 'ru', text: '' },
          { locale: 'en', text: '' },
        ],
      },
      {
        isCorrect: false,
        image: '',
        translations: [
          { locale: 'uz', text: '' },
          { locale: 'ru', text: '' },
          { locale: 'en', text: '' },
        ],
      },
    ]);
    setView('form');
  };

  const handleOpenEdit = (q: ExamQuestion) => {
    setSelectedQuestion(q);
    setType((q.type as any) || 'SINGLE_CHOICE');
    setPoints(q.points || 1);
    setQuestionImage(q.image || '');
    setQuestionTranslations([
      { locale: 'uz', text: q.text || '' },
      { locale: 'ru', text: '' },
      { locale: 'en', text: '' },
    ]);

    if (q.options && q.options.length > 0) {
      setOptions(
        q.options.map((opt) => ({
          id: opt.id,
          isCorrect: opt.isCorrect,
          image: opt.image || '',
          translations: [
            { locale: 'uz', text: opt.text || '' },
            { locale: 'ru', text: '' },
            { locale: 'en', text: '' },
          ],
        }))
      );
    } else {
      setOptions([]);
    }
    setView('form');
  };

  const handleAddOption = () => {
    setOptions([
      ...options,
      {
        isCorrect: false,
        image: '',
        translations: [
          { locale: 'uz', text: '' },
          { locale: 'ru', text: '' },
          { locale: 'en', text: '' },
        ],
      },
    ]);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleOptionTranslationChange = (
    optIndex: number,
    locale: 'uz' | 'ru' | 'en',
    text: string
  ) => {
    const newOptions = [...options];
    newOptions[optIndex].translations = newOptions[optIndex].translations.map((t) =>
      t.locale === locale ? { ...t, text } : t
    );
    setOptions(newOptions);
  };

  const handleOptionImageChange = (optIndex: number, image: string) => {
    const newOptions = [...options];
    newOptions[optIndex].image = image;
    setOptions(newOptions);
  };

  const handleSetCorrectOption = (optIndex: number) => {
    const newOptions = [...options];
    if (type === 'SINGLE_CHOICE') {
      newOptions.forEach((opt, i) => {
        opt.isCorrect = i === optIndex;
      });
    } else {
      newOptions[optIndex].isCorrect = !newOptions[optIndex].isCorrect;
    }
    setOptions(newOptions);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (options.length < 2) {
      toast({
        title: 'Xatolik',
        description: 'Kamida 2 ta variant bo\'lishi kerak',
        variant: 'destructive',
      });
      return;
    }

    if (!options.some((o) => o.isCorrect)) {
      toast({
        title: 'Xatolik',
        description: 'Kamida bitta to\'g\'ri javobni belgilang',
        variant: 'destructive',
      });
      return;
    }

    if (selectedQuestion?.id) {
      const payload = {
        type,
        points: Number(points) || 1,
        image: questionImage || undefined,
        translations: questionTranslations,
        options: options.map((opt) => ({
          id: opt.id,
          isCorrect: opt.isCorrect,
          image: opt.image || undefined,
          translations: opt.translations,
        })),
      };

      updateQuestion(
        { questionId: selectedQuestion.id, data: payload },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Savol yangilandi' });
            setView('list');
            refetch();
          },
          onError: (err: any) => {
            toast({
              title: 'Xatolik',
              description: err?.response?.data?.message || 'Savolni yangilab bo\'lmadi',
              variant: 'destructive',
            });
          },
        }
      );
    } else {
      const payload = {
        questions: [
          {
            type,
            points: Number(points) || 1,
            image: questionImage || undefined,
            translations: questionTranslations,
            options: options.map((opt) => ({
              isCorrect: opt.isCorrect,
              image: opt.image || undefined,
              translations: opt.translations,
            })),
          },
        ],
      };

      addQuestions(
        { examId, data: payload },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Yangi savol qo\'shildi' });
            setView('list');
            refetch();
          },
          onError: (err: any) => {
            toast({
              title: 'Xatolik',
              description: err?.response?.data?.message || 'Savol qo\'shib bo\'lmadi',
              variant: 'destructive',
            });
          },
        }
      );
    }
  };

  const handleDelete = () => {
    if (!questionToDelete) return;
    deleteQuestion(questionToDelete.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: 'Savol o\'chirildi' });
        setIsDeleteOpen(false);
        setQuestionToDelete(null);
        refetch();
      },
      onError: (err: any) => {
        toast({
          title: 'Xatolik',
          description: err?.response?.data?.message || 'Savolni o\'chirib bo\'lmadi',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl p-6 custom-scrollbar">
        <DialogHeader className="pr-12">
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HelpCircle className="size-5 text-primary" />
              <span>{examName ? `${examName} — Savollar` : 'Imtihon Savollari'}</span>
            </div>
            {view === 'form' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setView('list')}
                className="gap-1 text-slate-500 rounded-xl"
              >
                <ArrowLeft className="size-4" /> Ro'yxatga qaytish
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {view === 'list' ? (
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">
                Jami savollar: <strong className="text-slate-900">{questionsList.length} ta</strong>
              </span>
              <Button
                onClick={handleOpenCreate}
                className="gap-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs h-9 px-3"
              >
                <Plus className="size-4" /> Yangi savol qo'shish
              </Button>
            </div>

            {isLoading ? (
              <div className="py-12"><Loader /></div>
            ) : questionsList.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/50">
                <HelpCircle className="size-10 mx-auto text-slate-300 mb-2" />
                <p className="font-semibold text-slate-700">Savollar topilmadi</p>
                <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                  Imtihon topshiruvchilar uchun test savollari va javob variantlarini qo'shing.
                </p>
                <Button onClick={handleOpenCreate} variant="outline" className="mt-4 gap-1.5 rounded-xl text-xs">
                  <Plus className="size-3.5" /> Savol yaratish
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {questionsList.map((q, idx) => (
                  <div
                    key={q.id}
                    className="p-4 rounded-xl border border-slate-200/80 bg-white hover:border-slate-300 transition-all space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5 flex-1">
                        <span className="size-6 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-slate-900">{q.text || 'Matnsiz savol'}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              {q.points || 1} ball
                            </span>
                            {q.type && (
                              <span className="text-[11px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                {q.type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(q)}
                          className="size-8 text-slate-500 hover:text-primary rounded-lg"
                        >
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setQuestionToDelete(q);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-500 hover:text-red-500 rounded-lg"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>

                    {q.image && (
                      <div className="max-w-xs rounded-lg overflow-hidden border border-slate-200">
                        <img
                          src={getMediaUrl(q.image)}
                          alt="Savol rasmi"
                          className="max-h-36 w-auto object-contain bg-slate-50"
                        />
                      </div>
                    )}

                    {/* Options list */}
                    {q.options && q.options.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-slate-100">
                        {q.options.map((opt, oIdx) => (
                          <div
                            key={opt.id || oIdx}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded-lg text-xs border',
                              opt.isCorrect
                                ? 'bg-emerald-50/70 border-emerald-200 text-emerald-900 font-medium'
                                : 'bg-slate-50 border-slate-100 text-slate-600'
                            )}
                          >
                            <span
                              className={cn(
                                'size-4 rounded-full flex items-center justify-center text-[10px] shrink-0 font-bold',
                                opt.isCorrect
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-slate-200 text-slate-600'
                              )}
                            >
                              {String.fromCharCode(65 + oIdx)}
                            </span>
                            {opt.image && (
                              <img
                                src={getMediaUrl(opt.image)}
                                alt="Variant"
                                className="size-6 rounded object-cover border border-slate-200"
                              />
                            )}
                            <span className="truncate flex-1">{opt.text || 'Matnsiz variant'}</span>
                            {opt.isCorrect && (
                              <CheckCircle2 className="size-3.5 text-emerald-600 shrink-0" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
<Pagination currentPage={page} setCurrentPage={setPage} paginationInfo={questionsData?.meta?.pagination ?? questionsData?.data?.meta?.pagination} />
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Savol turi (Type)</Label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as any)}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="SINGLE_CHOICE">Yagona tanlov (SINGLE_CHOICE)</option>
                  <option value="MULTIPLE_CHOICE">Ko'p tanlov (MULTIPLE_CHOICE)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Ball (Points)</Label>
                <Input
                  type="number"
                  min={1}
                  value={points}
                  onChange={(e) => setPoints(Number(e.target.value))}
                  className="rounded-xl bg-white"
                  required
                />
              </div>

              <div className="col-span-2">
                <FileUploadInput
                  label="Savol Rasmi (Ixtiyoriy)"
                  value={questionImage}
                  onChange={(url) => setQuestionImage(url)}
                />
              </div>
            </div>

            {/* Question Translations */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-900 block">
                Savol matni (UZ / RU / EN)
              </Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>
                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const val = questionTranslations.find((t) => t.locale === locale)?.text || '';
                  return (
                    <TabsContent key={locale} value={locale} className="pt-2">
                      <Input
                        placeholder={`Savol matnini kiriting (${locale.toUpperCase()})...`}
                        value={val}
                        onChange={(e) => {
                          setQuestionTranslations((prev) =>
                            prev.map((t) => (t.locale === locale ? { ...t, text: e.target.value } : t))
                          );
                        }}
                        className="rounded-xl"
                        required={locale === 'uz'}
                      />
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            {/* Options */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold text-slate-900">
                  Javob variantlari (Kamida 2 ta)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddOption}
                  className="gap-1 text-xs rounded-xl h-8"
                >
                  <Plus className="size-3.5" /> Variant qo'shish
                </Button>
              </div>

              <div className="space-y-3">
                {options.map((opt, optIdx) => (
                  <div
                    key={optIdx}
                    className={cn(
                      'p-3.5 rounded-xl border transition-all space-y-2',
                      opt.isCorrect
                        ? 'border-emerald-300 bg-emerald-50/30'
                        : 'border-slate-200 bg-slate-50/50'
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSetCorrectOption(optIdx)}
                          className={cn(
                            'size-6 rounded-full flex items-center justify-center text-xs font-bold transition-all',
                            opt.isCorrect
                              ? 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                          )}
                        >
                          {String.fromCharCode(65 + optIdx)}
                        </button>
                        <span className="text-xs font-semibold text-slate-700">
                          {opt.isCorrect ? '✓ To\'g\'ri javob' : 'Noto\'g\'ri variant'}
                        </span>
                      </div>

                      {options.length > 2 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveOption(optIdx)}
                          className="size-7 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>

                    <Tabs defaultValue="uz" className="w-full">
                      <TabsList className="grid grid-cols-3 bg-slate-200/60 p-0.5 rounded-lg h-7">
                        <TabsTrigger value="uz" className="rounded-md text-[11px] h-6">UZ</TabsTrigger>
                        <TabsTrigger value="ru" className="rounded-md text-[11px] h-6">RU</TabsTrigger>
                        <TabsTrigger value="en" className="rounded-md text-[11px] h-6">EN</TabsTrigger>
                      </TabsList>
                      {(['uz', 'ru', 'en'] as const).map((locale) => {
                        const optVal = opt.translations.find((t) => t.locale === locale)?.text || '';
                        return (
                          <TabsContent key={locale} value={locale} className="pt-2">
                            <Input
                              placeholder={`Variant matni (${locale.toUpperCase()})...`}
                              value={optVal}
                              onChange={(e) => handleOptionTranslationChange(optIdx, locale, e.target.value)}
                              className="rounded-xl bg-white text-xs h-9"
                              required={locale === 'uz'}
                            />
                          </TabsContent>
                        );
                      })}
                    </Tabs>

                    <div className="pt-1">
                      <FileUploadInput
                        label="Variant Rasmi (Ixtiyoriy)"
                        value={opt.image || ''}
                        onChange={(url) => handleOptionImageChange(optIdx, url)}
                        placeholder="Variant uchun rasm tanlang..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => setView('list')}
                className="rounded-xl"
              >
                Bekor qilish
              </Button>
              <Button
                type="submit"
                disabled={isAdding || isUpdating}
                className="rounded-xl bg-primary text-white"
              >
                {isAdding || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </div>
          </form>
        )}

        <AlertDialog
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          alertTitle="Savolni o'chirishni tasdiqlaysizmi?"
          alertDescription="Ushbu savol va uning barcha javob variantlari o'chiriladi."
          alertActionTitle="O'chirish"
          alertCancel="Bekor qilish"
          alertActionFunction={handleDelete}
        />
      </DialogContent>
    </Dialog>
  );
};
